import { useEffect, useMemo, useState } from "react";
import { revalidateLogic, useForm, useSelector } from "@tanstack/react-form";
import { z } from "zod";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  PasswordRequirementsList,
  usePasswordRequirements,
  type ActivationLinkStatus,
  type AuthRepository,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PasswordField,
  Skeleton,
} from "@nasam/ui";

import { authRepository as defaultAuthRepository } from "../../auth";
import { AuthCardLayout, RequiredMark } from "./AuthCardLayout";
import { LinkStatusView } from "./LinkStatusScreen";

export interface ActivateAccountScreenProps {
  readonly authRepository?: AuthRepository;
  readonly initialToken?: string;
}

export function ActivateAccountScreen({
  authRepository = defaultAuthRepository,
  initialToken,
}: ActivateAccountScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  let queryToken: string | undefined;
  try {
    const location = useLocation();
    const tokenParam = new URLSearchParams(location.searchStr).get("token");
    if (tokenParam !== null && tokenParam.length > 0) {
      queryToken = tokenParam;
    }
  } catch {
    // If rendered outside router context
  }

  const token =
    initialToken ??
    queryToken ??
    (typeof window !== "undefined"
      ? (new URLSearchParams(window.location.search).get("token") ?? "")
      : "");

  const [checking, setChecking] = useState(true);
  const [linkStatus, setLinkStatus] = useState<ActivationLinkStatus | null>(
    null,
  );
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setLinkStatus("INVALID");
      setChecking(false);
      return;
    }

    async function checkLink() {
      try {
        const result = await authRepository.checkActivationLink({
          activationToken: token,
        });
        if (!cancelled) {
          setLinkStatus(result.status);
          setEmail(result.email);
          setLinkMessage(result.message);
        }
      } catch {
        if (!cancelled) {
          setLinkStatus("INVALID");
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    }

    void checkLink();

    return () => {
      cancelled = true;
    };
  }, [authRepository, token]);

  const schema = useMemo(
    () =>
      z
        .object({
          newPassword: z
            .string()
            .min(1, t("validation:passwordRequired"))
            .min(8, t("validation:passwordMinLength"))
            .regex(/[A-Z]/, t("validation:passwordUppercase"))
            .regex(/[a-z]/, t("validation:passwordLowercase"))
            .regex(/[0-9]/, t("validation:passwordNumber"))
            .regex(/[^A-Za-z0-9]/, t("validation:passwordSpecialChar")),
          confirmPassword: z
            .string()
            .min(1, t("validation:confirmPasswordRequired")),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t("validation:passwordsDoNotMatch"),
          path: ["confirmPassword"],
        }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    try {
      await authRepository.activateAccount({
        activationToken: token,
        newPassword: values.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : t("common:somethingWentWrong"),
      );
    }
  };

  const form = useForm({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    } satisfies FormValues,
    validationLogic: revalidateLogic({ mode: "change" }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });
  const newPassword = useSelector(
    form.store,
    (state) => state.values.newPassword,
  );
  const passwordRequirements = usePasswordRequirements(newPassword);

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  const handleResendActivation = async () => {
    if (email) {
      await authRepository.resendActivationLink({ email });
    }
  };

  if (checking) {
    return (
      <main
        role="status"
        aria-live="polite"
        className="animate-rise flex min-h-screen flex-col items-center justify-center gap-4 bg-page-background-light text-center"
      >
        <Skeleton aria-hidden="true" className="size-10 rounded-full" />
        <p className="text-body-m-r text-text-placeholder">
          {t("authentication:checkingLink")}
        </p>
      </main>
    );
  }

  if (linkStatus !== "VALID") {
    return (
      <LinkStatusView
        status={linkStatus ?? "INVALID"}
        mode="activation"
        email={email}
        message={linkMessage}
        onResendActivationLink={email ? handleResendActivation : undefined}
        onBackToLogin={() => {
          void navigate({ to: "/auth/login" });
        }}
      />
    );
  }

  if (success) {
    return (
      <AuthCardLayout
        title={t("authentication:activateAccountSuccessTitle")}
        illustrationSrc="/brand/password-illustration.png"
        footer={
          <Button
            type="button"
            variant="primary"
            className="h-13 w-full"
            onClick={() => {
              void navigate({ to: "/auth/login" });
            }}
          >
            {t("authentication:proceedToLogin")}
          </Button>
        }
      />
    );
  }

  return (
    <AuthCardLayout
      title={t("authentication:activateAccountTitle")}
      illustrationSrc="/brand/password-illustration.png"
      footer={
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              form="activate-account-form"
              variant="primary"
              className="h-13 w-full"
              disabled={isSubmitting}
            >
              <span
                key={isSubmitting ? "busy" : "idle"}
                className="animate-in fade-in-0 duration-150"
              >
                {isSubmitting
                  ? t("authentication:activatingAccountButton")
                  : t("authentication:activateAccountButton")}
              </span>
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <Form
        form={form}
        id="activate-account-form"
        className="flex w-full flex-col gap-4 px-4 md:px-13 py-2"
        noValidate
      >
        {errorMessage !== null && (
          <div
            role="alert"
            className="animate-in fade-in-0 slide-in-from-top-1 rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
          >
            {errorMessage}
          </div>
        )}

        <form.Field name="newPassword">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1">
                <FormLabel
                  htmlFor="activate-new-password"
                  className="flex items-center gap-1 text-body-m-r"
                >
                  {t("authentication:newPasswordLabel")}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <PasswordField
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    id="activate-new-password"
                    autoComplete="new-password"
                    placeholder={t("authentication:newPasswordPlaceholder")}
                    hidePasswordLabel={t("authentication:hidePassword")}
                    showPasswordLabel={t("authentication:showPassword")}
                    className="h-11 bg-container-hover px-4 py-2 text-start text-body-s-r placeholder:text-body-s-r"
                  />
                </FormControl>
                <FormMessage className="text-start" />
              </FormItem>
            </FormField>
          )}
        </form.Field>

        <form.Field name="confirmPassword">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1 [animation-delay:60ms]">
                <FormLabel
                  htmlFor="activate-confirm-password"
                  className="flex items-center gap-1 text-body-m-r"
                >
                  {t("authentication:confirmPasswordLabel")}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <PasswordField
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    id="activate-confirm-password"
                    autoComplete="new-password"
                    placeholder={t("authentication:confirmPasswordPlaceholder")}
                    hidePasswordLabel={t("authentication:hidePassword")}
                    showPasswordLabel={t("authentication:showPassword")}
                    className="h-11 bg-container-hover px-4 py-2 text-start text-body-s-r placeholder:text-body-s-r"
                  />
                </FormControl>
                <FormMessage className="text-start" />
              </FormItem>
            </FormField>
          )}
        </form.Field>

        <PasswordRequirementsList requirements={passwordRequirements} />
      </Form>
    </AuthCardLayout>
  );
}
